import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { AccountFilterKeywordsService } from "../api";
import { PlanType, PlanTypeName, IsActive } from "../constants";
import { isStringArraysEqual } from "../libs";

import { accountsListActions } from "./accountsList";

interface CheckItem {
  label: string;
  value: string;
  isChecked: boolean;
}

interface FilterAccountState {
  corporateNames: CheckItem[];
  storeNames: CheckItem[];
  registrationRoutes: CheckItem[];
  planTypes: CheckItem[];
  isActives: CheckItem[];
}

interface RootState {
  filterAccount: FilterAccountState;
}

interface ThunkAPI {
  getState: () => RootState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  dispatch: (value: unknown) => unknown;
}

const getFilters = createAsyncThunk(
  "getFilters",
  async (_: void, thunkAPI: ThunkAPI) => {
    const res = await AccountFilterKeywordsService.accountFilterKeywords();
    const { corporate_names, store_names, registration_routes } = res;
    const { corporateNames, storeNames, registrationRoutes } =
      thunkAPI.getState().filterAccount;
    if (
      isSameCheckItemsVsNames(corporateNames, corporate_names) &&
      isSameCheckItemsVsNames(storeNames, store_names) &&
      isSameCheckItemsVsNames(registrationRoutes, registration_routes)
    )
      return;

    const newCorporateNames = getCheckItemsFromNames(corporate_names);
    thunkAPI.dispatch(
      filterAccountActions.setCorporateNames(newCorporateNames),
    );
    const newStoreNames = getCheckItemsFromNames(store_names);
    thunkAPI.dispatch(filterAccountActions.setStoreNames(newStoreNames));
    const newRegistrationRoutes = getCheckItemsFromNames(registration_routes);
    thunkAPI.dispatch(
      filterAccountActions.setRegistrationRoutes(newRegistrationRoutes),
    );
    const newPlanTypes = getCheckItemsFromOptions(PlanType, PlanTypeName);
    thunkAPI.dispatch(filterAccountActions.setPlanTypes(newPlanTypes));
    const newIsActives = getCheckItemsFromOptions(IsActive);
    thunkAPI.dispatch(filterAccountActions.setIsActives(newIsActives));
  },
);

function isSameCheckItemsVsNames(checkItems: CheckItem[], names: string[]) {
  const nnnames = names.filter((name) => name);
  const existingCheckItemsNames = checkItems.map(
    (checkItem) => checkItem.label,
  );
  return isStringArraysEqual(nnnames, existingCheckItemsNames);
}

function getCheckItemsFromNames(names: string[]) {
  return names
    .filter((name) => name)
    .map((name: string) => {
      return { label: name, value: name, isChecked: false };
    });
}

function getCheckItemsFromOptions(options: Record<string, string | number>, displayNames?: Record<string, string>) {
  const checkItems: CheckItem[] = [];
  Object.keys(options).forEach((key) => {
    checkItems.push({
      label: displayNames?.[key] ?? key,
      value: String(options[key]),
      isChecked: false,
    });
  });
  return checkItems;
}

const applyFilter = createAsyncThunk(
  "applyFilter",
  async (_: void, thunkAPI: ThunkAPI) => {
    const {
      corporateNames,
      storeNames,
      registrationRoutes,
      planTypes,
      isActives,
    } = thunkAPI.getState().filterAccount;

    thunkAPI.dispatch(
      accountsListActions.setCorporateNameFilter(getFilterStr(corporateNames)),
    );
    thunkAPI.dispatch(
      accountsListActions.setStoreNameFilter(getFilterStr(storeNames)),
    );
    thunkAPI.dispatch(
      accountsListActions.setRegistrationRouteFilter(
        getFilterStr(registrationRoutes),
      ),
    );
    thunkAPI.dispatch(
      accountsListActions.setPlanTypesFilter(getFilterStr(planTypes)),
    );
    thunkAPI.dispatch(
      accountsListActions.setIsActiveFilter(getFilterStr(isActives)),
    );
  },
);

function getFilterStr(checkItems: CheckItem[]) {
  return checkItems
    .filter((item) => item.isChecked)
    .map((checkItem: CheckItem) => checkItem.value)
    .join(",");
}

function setCheckedForList(
  checkItems: CheckItem[],
  newCheckItems: CheckItem[],
) {
  const checkedValues = newCheckItems.map((item) => item.value);
  checkItems.forEach((checkItem) => {
    if (!checkedValues.includes(checkItem.value)) {
      checkItem.isChecked = false;
      return;
    }

    checkItem.isChecked = true;
  });
}

const initialState: FilterAccountState = {
  corporateNames: [] as CheckItem[],
  storeNames: [] as CheckItem[],
  registrationRoutes: [] as CheckItem[],
  planTypes: [] as CheckItem[],
  isActives: [] as CheckItem[],
};

const filterAccountSlice = createSlice({
  name: "filterAccount",
  initialState,
  reducers: {
    setCorporateNames: (state, action: PayloadAction<CheckItem[]>) => {
      state.corporateNames = action.payload;
    },
    switchCorporateNameChecked: (state, action: PayloadAction<CheckItem[]>) => {
      setCheckedForList(state.corporateNames, action.payload);
    },
    setStoreNames: (state, action: PayloadAction<CheckItem[]>) => {
      state.storeNames = action.payload;
    },
    switchStoreNameChecked: (state, action: PayloadAction<CheckItem[]>) => {
      setCheckedForList(state.storeNames, action.payload);
    },
    setRegistrationRoutes: (state, action: PayloadAction<CheckItem[]>) => {
      state.registrationRoutes = action.payload;
    },
    switchRegistrationRouteChecked: (
      state,
      action: PayloadAction<CheckItem[]>,
    ) => {
      setCheckedForList(state.registrationRoutes, action.payload);
    },
    setPlanTypes: (state, action: PayloadAction<CheckItem[]>) => {
      state.planTypes = action.payload;
    },
    switchPlanTypeChecked: (state, action: PayloadAction<CheckItem[]>) => {
      setCheckedForList(state.planTypes, action.payload);
    },
    setIsActives: (state, action: PayloadAction<CheckItem[]>) => {
      state.isActives = action.payload;
    },
    switchIsActiveChecked: (state, action: PayloadAction<CheckItem[]>) => {
      setCheckedForList(state.isActives, action.payload);
    },
    setInitState: () => {
      return initialState;
    },
  },
});

const filterAccountActions = {
  ...filterAccountSlice.actions,
  getFilters,
  applyFilter,
};
export { filterAccountActions };
export default filterAccountSlice.reducer;
